import { LoadingState } from "molecules/loadingState/LoadingState";
import { MissingDataState } from "molecules/missingDataState/MissingDataState";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { TypedUseQueryHookResult } from "@reduxjs/toolkit/query/react";

const supportEmail = "support@exopen.se";

type QueriesMap = {
	[key: string]: TypedUseQueryHookResult<any, any, any>;
};

type QueryReturnType<T> = T extends TypedUseQueryHookResult<any, any, any>
	? T extends { data?: infer R }
		? Exclude<R, undefined>
		: never
	: never;

type QueriesReturnTypes<T> = T extends QueriesMap
	? {
			[K in keyof T]: QueryReturnType<T[K]>;
		}
	: never;

type Props<T> = {
	queries: T;
	render: (
		data: QueriesReturnTypes<T>,
		params: { isFetching: boolean },
	) => ReactNode;
	renderLoading?: () => ReactNode;
	renderError?: (error: unknown) => ReactNode;
};

export const QueriesResult = <T extends QueriesMap>({
	queries,
	render,
	renderLoading = () => <LoadingState />,
	renderError,
}: Props<T>) => {
	const { t } = useTranslation();
	const defaultError = (
		<>
			{t("Something went wrong. Try reloading the page, or contact support:")}{" "}
			<a className="underline" href={`mailto:${supportEmail}`}>
				{supportEmail}
			</a>
		</>
	);
	const resolvedRenderError =
		renderError ??
		(() => (
			<MissingDataState title={t("No data found")} description={defaultError} />
		));

	const queryResults = Object.values(queries);
	if (queryResults.some((query) => query.isLoading)) {
		return <>{renderLoading()}</>;
	}

	if (
		queryResults.some((query) => query.isError) ||
		queryResults.some((query) => !query.data)
	) {
		return (
			<>
				{resolvedRenderError(queryResults.find((query) => query.error)?.error)}
			</>
		);
	}

	const resultObject = Object.fromEntries(
		Object.entries(queries).map(([key, query]) => [key, query.data]),
	) as QueriesReturnTypes<T>;

	return (
		<>
			{render(resultObject, {
				isFetching: queryResults.some((query) => query.isFetching),
			})}
		</>
	);
};
